// CB:BEGIN pm-channel-landing
/**
 * Property Manager Landing Page (/pm)
 *
 * Public-facing page targeted at property managers and facility operators.
 * Explains the CoreBids value proposition for multi-property portfolios:
 * - Get multiple vetted bids without chasing contractors
 * - Track repairs across all properties in one place
 * - Keep owners and stakeholders informed automatically
 *
 * CTA → /pm-start (pm-signup screen): in-app signup, then onNavigate('pm-onboarding').
 * Do not use /sign-up — it is not mapped in getScreenFromUrl and falls through to landing.
 * If already signed in as PM → redirect to /pm-dashboard
 */

import React, { useEffect, useRef, useState } from 'react';
import { useUser } from '@clerk/clerk-react';
import { getApiBaseUrl } from '../lib/getApiUrl';
import ContentLimiter from '../components/layout/ContentLimiter';
import { useDevice } from '../hooks/useDevice';
import {
  ArrowRight,
  Building2,
  ClipboardList,
  CheckCircle2,
  BarChart2,
  ShieldCheck,
  Clock,
} from 'lucide-react';

const BRAND_GREEN = '#123c3b';

interface PropertyManagerLandingProps {
  onNavigate: (screen: string) => void;
}

const PropertyManagerLanding: React.FC<PropertyManagerLandingProps> = ({ onNavigate }) => {
  const { isMobileLayout } = useDevice();
  const { user, isSignedIn } = useUser();
  const processedRef = useRef(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  // If user is already signed in as a PM, redirect straight to the dashboard
  useEffect(() => {
    const checkRole = async () => {
      if (processedRef.current) return;
      if (!isSignedIn || !user) return;
      processedRef.current = true;
      try {
        const baseUrl = getApiBaseUrl();
        const token = await (window as any).Clerk?.session?.getToken?.() ?? null;
        const resp = await fetch(`${baseUrl.replace(/\/$/, '')}/api/me`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (resp.ok) {
          const data = await resp.json();
          if (data?.role === 'PROPERTY_MANAGER') {
            onNavigate('pm-dashboard');
          }
        }
      } catch (e) {
        // Non-fatal — stay on landing page
      }
    };
    checkRole();
  }, [isSignedIn, user, onNavigate]);

  // CB:BEGIN pm-cta-pm-start
  const handlePrimaryCta = () => {
    onNavigate('pm-signup');
  };
  // CB:END pm-cta-pm-start
  const handleBackHome = () => onNavigate('landing');

  // ------- content data -------

  const heroCards = [
    {
      icon: <Building2 size={44} style={{ color: '#fbbf24' }} />,
      title: 'One Place for Every Property',
      desc: 'Submit a project for any address in your portfolio and track it to completion — no spreadsheets, no follow-up calls.',
    },
    {
      icon: <ClipboardList size={44} style={{ color: '#60a5fa' }} />,
      title: 'Multiple Vetted Bids, Fast',
      desc: 'CoreBids matches your project with qualified local contractors and collects competing bids. You compare and decide.',
    },
    {
      icon: <BarChart2 size={44} style={{ color: '#34d399' }} />,
      title: 'Full Portfolio Visibility',
      desc: 'See every open project, bid status, and active work across your portfolio from a single operations dashboard.',
    },
  ];

  const steps = [
    {
      num: '01',
      icon: <Building2 size={30} style={{ color: BRAND_GREEN }} />,
      title: 'Create Your Account',
      desc: 'Sign up as a property manager. We create your portfolio account immediately — no approval wait.',
    },
    {
      num: '02',
      icon: <ClipboardList size={30} style={{ color: BRAND_GREEN }} />,
      title: 'Add Properties',
      desc: 'Add properties to your portfolio. Each property gets its own project history.',
    },
    {
      num: '03',
      icon: <CheckCircle2 size={30} style={{ color: BRAND_GREEN }} />,
      title: 'Submit a Project',
      desc: 'Describe the work needed — roof, siding, gutters. Set a priority and requested response timeline.',
    },
    {
      num: '04',
      icon: <ShieldCheck size={30} style={{ color: BRAND_GREEN }} />,
      title: 'Compare Bids and Award',
      desc: 'Review contractor bids side by side. Select the best one and the project moves forward with escrow-protected payments.',
    },
  ];

  const benefits = [
    {
      icon: <Clock size={28} style={{ color: BRAND_GREEN }} />,
      title: 'Set Response Deadlines',
      desc: 'Assign a "Respond by" date when your situation requires fast turnaround. Contractors see the deadline in their bid feed.',
    },
    {
      icon: <Building2 size={28} style={{ color: BRAND_GREEN }} />,
      title: 'Portfolio-Level Reporting',
      desc: 'Track spend by property, review contractor performance across jobs, and keep owners informed with clean status updates.',
    },
    {
      icon: <ShieldCheck size={28} style={{ color: BRAND_GREEN }} />,
      title: 'Escrow-Protected Payments',
      desc: 'Funds are held in escrow and only released at milestone approval. Contractors get paid when work is verified.',
    },
    {
      icon: <CheckCircle2 size={28} style={{ color: BRAND_GREEN }} />,
      title: 'Priority Flags for Urgent Work',
      desc: "Flag projects as Emergency or High Priority. Contractors see the urgency signal and can respond accordingly.",
    },
  ];

  // CB:BEGIN pm-channel-faq
  // Ordered to address the highest-friction PM objections first, then operational questions.
  const faqs = [
    {
      q: 'Do I need to replace my current contractors?',
      a: 'No. CoreBids works alongside your existing vendor relationships. Use it for overflow work, competitive comparison, or jobs where your regular contractors are unavailable. There is no exclusivity requirement.',
    },
    {
      q: 'Is there a cost for property managers?',
      a: 'Property manager accounts are free. The platform fee is applied to project payments — your contractor bids already account for this, so there is no separate charge to you.',
    },
    {
      q: 'Can I use CoreBids only for certain types of jobs?',
      a: 'Absolutely. Many property managers use CoreBids specifically for storm damage, emergency repairs, or one-off exterior work — rather than replacing every vendor relationship. Start with a single category and expand when it makes sense.',
    },
    {
      q: 'How fast do contractors typically respond?',
      a: 'Most projects receive bids within 24–72 hours of being posted. If you set a "Respond by" deadline, that date is visible in the contractor feed and typically accelerates responses on time-sensitive work.',
    },
    {
      q: 'How is this different from just calling contractors directly?',
      a: 'CoreBids brings multiple qualified contractors to you with competing bids instead of requiring you to call each one, wait for callbacks, and manually compare estimates. You get structured bids with line items, timelines, and warranties — all in one place.',
    },
    {
      q: 'Are payments protected?',
      a: 'Milestone-based escrow is built into the platform. Funds are deposited once a contractor is selected, then released at each approved project milestone. You never pay for work that has not been completed and verified.',
    },
    {
      q: 'Is CoreBids better for emergency repairs or planned work?',
      a: 'Both. For planned work, use Normal priority and give contractors a few days to respond. For urgent situations, use Emergency or High priority with a tight response deadline — contractors see the urgency flag prominently in their bid feed.',
    },
    {
      q: 'Can I manage projects for multiple properties at once?',
      a: 'Yes. Your dashboard shows all active projects across every property, filtered by status, priority, or address. You can have multiple concurrent projects and track each one independently.',
    },
    {
      q: 'Can I track all my properties in one account?',
      a: 'Yes. Your account links to a portfolio of properties. Each property has its own project history. Your dashboard shows everything across the portfolio in a single view, filterable by property, status, or priority.',
    },
    {
      q: 'How do contractors know this is a property manager project?',
      a: 'Every project is clearly tagged "Property Manager" in the contractor bid feed. Contractors can see your requested response timeline and any site access notes you provide.',
    },
    {
      q: 'What types of work can I submit?',
      a: 'Currently: roofing, siding, gutters, windows, doors, and exterior painting. The platform is expanding — contact us if you have volume work in other categories.',
    },
  ];
  // CB:END pm-channel-faq

  // CB:BEGIN pm-channel-who-for
  const whoForItems = [
    { title: 'Small Property Management Firms', desc: 'Running 5–50 units across multiple addresses and tired of managing contractor relationships one call at a time.' },
    { title: 'Local Portfolio Investors', desc: 'Owners managing multiple rentals who need fast, reliable repair turnaround without a full-time maintenance staff.' },
    { title: 'HOA and Community Managers', desc: 'Boards and managers who need documented bids, milestone tracking, and spend visibility for member reporting.' },
    { title: 'Commercial Building Operators', desc: 'Office and retail property teams who need exterior work quoted quickly with verifiable contractor credentials.' },
  ];

  const useCases = [
    { label: 'Storm Damage', desc: 'File an emergency project immediately after a weather event. Set a priority flag and response deadline.' },
    { label: 'Roof Replacement', desc: 'Get competing bids for planned replacements across your portfolio. Compare scope, materials, and timelines.' },
    { label: 'Siding & Exterior', desc: 'Submit siding repair or replacement projects. Contractors quote with structured line items.' },
    { label: 'Tenant Turnover Repairs', desc: 'Handle unit-level exterior repairs between tenancies. Keep owners informed with project status updates.' },
    { label: 'Vendor Overflow', desc: 'Use CoreBids when your regular contractors are unavailable or for competitive pricing checks on larger jobs.' },
  ];
  // CB:END pm-channel-who-for

  const stats = [
    { big: 'Up to 3', label: 'Bids per project' },
    { big: 'Free', label: 'PM account' },
    { big: 'Escrow', label: 'Payment protection' },
  ];

  return (
    <div style={{ fontFamily: 'Inter, system-ui, -apple-system, Segoe UI, sans-serif', background: '#fff' }}>
      <style>{`
        .cb-pm-btn-primary:hover { transform: translateY(-2px); box-shadow: 0 12px 32px rgba(0,0,0,0.25); }
        .cb-pm-btn-secondary:hover { background-color: rgba(255,255,255,0.10); border-color: rgba(255,255,255,0.50); }
        .cb-pm-hero-card:hover { background-color: rgba(255,255,255,0.25); transform: translateY(-4px); }
        .cb-pm-step-card:hover { border-color: ${BRAND_GREEN}; transform: translateY(-4px); box-shadow: 0 12px 32px rgba(18,60,59,0.10); }
        .cb-pm-benefit-card:hover { box-shadow: 0 8px 24px rgba(18,60,59,0.10); border-color: ${BRAND_GREEN}; }
      `}</style>

      {/* Simple nav header */}
      <header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          background: '#fff',
          borderBottom: '1px solid #e2e8f0',
          padding: '0 24px',
        }}
      >
        <ContentLimiter>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              height: 64,
            }}
          >
            <button
              onClick={handleBackHome}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: 20,
                fontWeight: 800,
                color: BRAND_GREEN,
                letterSpacing: '-0.03em',
              }}
            >
              CoreBids
            </button>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <button
                onClick={handleBackHome}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 14,
                  color: '#64748b',
                  padding: '8px 12px',
                }}
              >
                Back to Home
              </button>
              <button
                onClick={() => onNavigate('sign-in')}
                style={{
                  background: 'none',
                  border: `1px solid ${BRAND_GREEN}`,
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 600,
                  color: BRAND_GREEN,
                  padding: '8px 16px',
                  borderRadius: 8,
                }}
              >
                Log In
              </button>
              <button
                className="cb-pm-btn-primary"
                onClick={handlePrimaryCta}
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  padding: '10px 20px',
                  backgroundColor: BRAND_GREEN,
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
              >
                Get Started
              </button>
            </div>
          </div>
        </ContentLimiter>
      </header>

      {/* Hero */}
      <section
        style={{
          position: 'relative',
          padding: isMobileLayout ? '56px 16px 64px' : '100px 40px 120px',
          background: 'linear-gradient(135deg, #123c3b 0%, #0f5251 50%, #1a6b69 100%)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(circle at 20% 80%, rgba(230,245,239,0.10) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(230,245,239,0.10) 0%, transparent 50%)',
            pointerEvents: 'none',
          }}
        />
        <ContentLimiter>
          <div style={{ position: 'relative', zIndex: 2, textAlign: 'center', maxWidth: 1000, margin: '0 auto' }}>
            <div
              style={{
                display: 'inline-block',
                background: 'rgba(255,255,255,0.15)',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: 24,
                padding: '6px 16px',
                fontSize: 13,
                fontWeight: 600,
                color: 'rgba(255,255,255,0.9)',
                marginBottom: 24,
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
              }}
            >
              For Property Managers
            </div>

            <h1
              style={{
                fontSize: isMobileLayout ? 38 : 'clamp(48px, 6vw, 70px)',
                fontWeight: 900,
                color: '#fff',
                marginBottom: 24,
                lineHeight: 1.1,
                letterSpacing: '-0.02em',
              }}
            >
              Stop Chasing Contractors.{' '}
              <span
                style={{
                  background: 'linear-gradient(135deg, #e6f5ef, #a7f3d0)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Start Managing.
              </span>
            </h1>

            <p
              style={{
                fontSize: isMobileLayout ? 18 : 22,
                color: 'rgba(255,255,255,0.9)',
                lineHeight: 1.6,
                maxWidth: 680,
                margin: '0 auto 28px',
              }}
            >
              Submit repair projects for any property in your portfolio. Get multiple vetted bids. Track everything in one place — without the vendor coordination burden.
            </p>

            {/* Hero proof bullets */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 10,
                marginBottom: 40,
              }}
            >
              {[
                '3+ competitive bids on every project — without calling a single contractor',
                'Set response deadlines. Flag emergencies. One dashboard for your whole portfolio.',
                'Milestone-based escrow. Contractors get paid when work is verified.',
              ].map((bullet) => (
                <div
                  key={bullet}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    maxWidth: 560,
                    textAlign: 'left',
                  }}
                >
                  <span style={{ color: '#a7f3d0', flexShrink: 0, marginTop: 3, fontSize: 15 }}>✓</span>
                  <span style={{ fontSize: isMobileLayout ? 14 : 15, color: 'rgba(255,255,255,0.85)', lineHeight: 1.5 }}>{bullet}</span>
                </div>
              ))}
            </div>

            <div
              style={{
                display: 'flex',
                gap: 16,
                justifyContent: 'center',
                alignItems: 'center',
                marginBottom: isMobileLayout ? 40 : 64,
                flexWrap: 'wrap',
              }}
            >
              <button
                className="cb-pm-btn-primary"
                onClick={handlePrimaryCta}
                style={{
                  fontSize: isMobileLayout ? 18 : 20,
                  fontWeight: 700,
                  padding: isMobileLayout ? '16px 22px' : '20px 48px',
                  backgroundColor: '#fff',
                  color: BRAND_GREEN,
                  border: 'none',
                  borderRadius: 12,
                  cursor: 'pointer',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                  transition: 'all 0.3s ease',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  minHeight: 44,
                }}
              >
                Sign Up Now <ArrowRight size={20} />
              </button>

              <button
                className="cb-pm-btn-secondary"
                onClick={handleBackHome}
                style={{
                  fontSize: isMobileLayout ? 16 : 18,
                  fontWeight: 600,
                  padding: isMobileLayout ? '14px 18px' : '18px 40px',
                  backgroundColor: 'transparent',
                  color: '#fff',
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderRadius: 12,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  minHeight: 44,
                }}
              >
                Back to Home
              </button>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                gap: 24,
                maxWidth: 1000,
                margin: '0 auto',
              }}
            >
              {heroCards.map((c) => (
                <div
                  key={c.title}
                  className="cb-pm-hero-card"
                  style={{
                    padding: '32px 24px',
                    backgroundColor: 'rgba(255,255,255,0.15)',
                    borderRadius: 20,
                    border: '1px solid rgba(255,255,255,0.2)',
                    backdropFilter: 'blur(10px)',
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                  }}
                >
                  <div
                    style={{
                      display: 'inline-flex',
                      padding: 16,
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      borderRadius: 16,
                      marginBottom: 20,
                    }}
                    aria-hidden="true"
                  >
                    {c.icon}
                  </div>
                  <h3 style={{ fontSize: 22, fontWeight: 700, color: '#fff', marginBottom: 12 }}>{c.title}</h3>
                  <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.85)', lineHeight: 1.6 }}>{c.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </ContentLimiter>
      </section>

      {/* Trust strip */}
      {/* CB:BEGIN pm-channel-trust-strip */}
      <section style={{ padding: isMobileLayout ? '32px 16px' : '40px 40px', background: '#0f2d2d', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        <ContentLimiter>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobileLayout ? '1fr 1fr' : 'repeat(4, 1fr)',
              gap: isMobileLayout ? 20 : 0,
              maxWidth: 900,
              margin: '0 auto',
              textAlign: 'center',
            }}
          >
            {[
              { value: 'Free', detail: 'PM account — no setup fee' },
              { value: '3+', detail: 'Vetted bids per project' },
              { value: 'Escrow', detail: 'Milestone-protected payments' },
              { value: 'No lock-in', detail: 'Keep your existing vendors' },
            ].map((item, i) => (
              <div
                key={item.value}
                style={{
                  padding: isMobileLayout ? '0' : '0 24px',
                  borderRight: (!isMobileLayout && i < 3) ? '1px solid rgba(255,255,255,0.12)' : 'none',
                }}
              >
                <div style={{ fontSize: isMobileLayout ? 22 : 26, fontWeight: 900, color: '#a7f3d0', marginBottom: 4 }}>{item.value}</div>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', lineHeight: 1.4 }}>{item.detail}</div>
              </div>
            ))}
          </div>
        </ContentLimiter>
      </section>
      {/* CB:END pm-channel-trust-strip */}

      {/* Problem / Solution */}
      <section style={{ padding: isMobileLayout ? '64px 16px' : '100px 40px', background: '#f8fafc' }}>
        <ContentLimiter>
          <div style={{ maxWidth: 900, margin: '0 auto' }}>
            <h2
              style={{
                fontSize: isMobileLayout ? 30 : 42,
                fontWeight: 900,
                color: '#0f172a',
                textAlign: 'center',
                marginBottom: 16,
              }}
            >
              Property repairs get messy fast.
            </h2>
            <p
              style={{
                fontSize: 18,
                color: '#64748b',
                textAlign: 'center',
                maxWidth: 620,
                margin: '0 auto 56px',
                lineHeight: 1.6,
              }}
            >
              Most property managers are running repair coordination on phone calls, group texts, and spreadsheets. It works — until it doesn't.
            </p>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: isMobileLayout ? '1fr' : '1fr 1fr',
                gap: 32,
              }}
            >
              {/* Before */}
              <div
                style={{
                  background: '#fff',
                  border: '2px solid #fde8e8',
                  borderRadius: 20,
                  padding: 32,
                }}
              >
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: '#dc2626',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    marginBottom: 20,
                  }}
                >
                  Without CoreBids
                </div>
                {[
                  'Call 3-5 contractors, wait for callbacks',
                  'Chase for estimates, compare inconsistent formats',
                  'No visibility until contractor shows up',
                  'Separate process for each property',
                  'Manual payment tracking, no milestone protection',
                  'Owner calls asking for status — no easy answer',
                ].map((item) => (
                  <div
                    key={item}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 12,
                      marginBottom: 14,
                      fontSize: 15,
                      color: '#475569',
                    }}
                  >
                    <span style={{ color: '#dc2626', flexShrink: 0, marginTop: 2 }}>✗</span>
                    {item}
                  </div>
                ))}
              </div>
              {/* After */}
              <div
                style={{
                  background: '#fff',
                  border: `2px solid ${BRAND_GREEN}`,
                  borderRadius: 20,
                  padding: 32,
                }}
              >
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: BRAND_GREEN,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    marginBottom: 20,
                  }}
                >
                  With CoreBids
                </div>
                {[
                  'Submit a project, bids come to you',
                  'Structured bid comparison with scope and timeline',
                  'Live project status in your dashboard',
                  'Portfolio-level view across all properties',
                  'Milestone-based escrow — pay only for completed work',
                  'Instant status for any stakeholder, any time',
                ].map((item) => (
                  <div
                    key={item}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 12,
                      marginBottom: 14,
                      fontSize: 15,
                      color: '#475569',
                    }}
                  >
                    <span style={{ color: BRAND_GREEN, flexShrink: 0, marginTop: 2 }}>✓</span>
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </ContentLimiter>
      </section>

      {/* How It Works */}
      <section style={{ padding: isMobileLayout ? '64px 16px' : '100px 40px', background: '#fff' }}>
        <ContentLimiter>
          <div style={{ maxWidth: 1000, margin: '0 auto' }}>
            <h2
              style={{
                fontSize: isMobileLayout ? 30 : 44,
                fontWeight: 900,
                color: '#0f172a',
                textAlign: 'center',
                marginBottom: 16,
              }}
            >
              How It Works
            </h2>
            <p
              style={{
                fontSize: 18,
                color: '#64748b',
                textAlign: 'center',
                maxWidth: 580,
                margin: '0 auto 64px',
              }}
            >
              Four steps from sign-up to an awarded bid.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {steps.map((s) => (
                <div
                  key={s.num}
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 8,
                    padding: '14px 20px',
                    background: '#f8fafc',
                    borderRadius: 10,
                    border: '1.5px solid #e2e8f0',
                    fontSize: 15,
                    lineHeight: 1.5,
                  }}
                >
                  <span style={{ fontWeight: 700, color: BRAND_GREEN, whiteSpace: 'nowrap', minWidth: 28 }}>{s.num}</span>
                  <span style={{ fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap' }}>{s.title}</span>
                  <span style={{ color: '#64748b' }}>— {s.desc}</span>
                </div>
              ))}
            </div>
          </div>
        </ContentLimiter>
      </section>

      {/* Stats bar */}
      <section style={{ padding: isMobileLayout ? '48px 16px' : '72px 40px', background: '#e6f5ef' }}>
        <ContentLimiter>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: 40,
              maxWidth: 900,
              margin: '0 auto',
              textAlign: 'center',
            }}
          >
            {stats.map((x) => (
              <div key={x.label}>
                <div
                  style={{
                    fontSize: isMobileLayout ? 48 : 60,
                    fontWeight: 900,
                    color: BRAND_GREEN,
                    marginBottom: 8,
                  }}
                >
                  {x.big}
                </div>
                <div style={{ fontSize: 17, fontWeight: 600, color: '#334155' }}>{x.label}</div>
              </div>
            ))}
          </div>
        </ContentLimiter>
      </section>

      {/* Benefits */}
      <section style={{ padding: isMobileLayout ? '64px 16px' : '100px 40px', background: '#fff' }}>
        <ContentLimiter>
          <div style={{ maxWidth: 1000, margin: '0 auto' }}>
            <h2
              style={{
                fontSize: isMobileLayout ? 30 : 44,
                fontWeight: 900,
                color: '#0f172a',
                textAlign: 'center',
                marginBottom: 16,
              }}
            >
              Built for Operational Scale
            </h2>
            <p
              style={{
                fontSize: 18,
                color: '#64748b',
                textAlign: 'center',
                maxWidth: 580,
                margin: '0 auto 64px',
              }}
            >
              Tools that reduce vendor management burden for teams managing multiple properties.
            </p>
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
            >
              {benefits.map((b) => (
                <div
                  key={b.title}
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 8,
                    padding: '14px 20px',
                    background: '#f8fafc',
                    borderRadius: 10,
                    border: '1.5px solid #e2e8f0',
                    fontSize: 15,
                    lineHeight: 1.5,
                  }}
                >
                  <span style={{ fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap' }}>{b.title}</span>
                  <span style={{ color: '#64748b' }}>— {b.desc}</span>
                </div>
              ))}
            </div>
          </div>
        </ContentLimiter>
      </section>

      {/* CB:BEGIN pm-channel-who-for-section */}
      {/* Who This Is For */}
      <section style={{ padding: isMobileLayout ? '64px 16px' : '96px 40px', background: '#fff' }}>
        <ContentLimiter>
          <h2 style={{ fontSize: isMobileLayout ? 28 : 40, fontWeight: 900, color: '#0f172a', textAlign: 'center', marginBottom: 12 }}>
            Who This Is For
          </h2>
          <p style={{ fontSize: 17, color: '#64748b', textAlign: 'center', maxWidth: 560, margin: '0 auto 52px', lineHeight: 1.6 }}>
            If you're responsible for exterior repairs across more than one address, this was built for you.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: isMobileLayout ? '1fr' : '1fr 1fr', gap: 20 }}>
            {whoForItems.map((item) => (
              <div
                key={item.title}
                style={{
                  background: '#f8fafc',
                  border: '1.5px solid #e2e8f0',
                  borderRadius: 16,
                  padding: '24px 28px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 16,
                }}
              >
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: BRAND_GREEN, flexShrink: 0, marginTop: 6 }} />
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>{item.title}</div>
                  <div style={{ fontSize: 14, color: '#64748b', lineHeight: 1.6 }}>{item.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </ContentLimiter>
      </section>

      {/* Common Use Cases */}
      <section style={{ padding: isMobileLayout ? '64px 16px' : '96px 40px', background: '#f8fafc' }}>
        <ContentLimiter>
          <h2 style={{ fontSize: isMobileLayout ? 28 : 40, fontWeight: 900, color: '#0f172a', textAlign: 'center', marginBottom: 12 }}>
            Common Use Cases
          </h2>
          <p style={{ fontSize: 17, color: '#64748b', textAlign: 'center', maxWidth: 520, margin: '0 auto 48px', lineHeight: 1.6 }}>
            Property managers use CoreBids across a range of exterior repair and replacement work.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {useCases.map((uc) => (
              <div
                key={uc.label}
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 8,
                  padding: '14px 20px',
                  background: '#fff',
                  border: '1.5px solid #e2e8f0',
                  borderRadius: 10,
                  fontSize: 15,
                  lineHeight: 1.5,
                }}
              >
                <span style={{ fontWeight: 700, color: BRAND_GREEN, whiteSpace: 'nowrap' }}>{uc.label}</span>
                <span style={{ color: '#475569' }}>— {uc.desc}</span>
              </div>
            ))}
          </div>
        </ContentLimiter>
      </section>

      {/* Social Proof Placeholder */}
      <section style={{ padding: isMobileLayout ? '64px 16px' : '96px 40px', background: '#fff' }}>
        <ContentLimiter>
          <div style={{ maxWidth: 800, margin: '0 auto', textAlign: 'center' }}>
            <div
              style={{
                display: 'inline-block',
                background: '#e6f5ef',
                color: BRAND_GREEN,
                fontSize: 12,
                fontWeight: 700,
                padding: '4px 14px',
                borderRadius: 20,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                marginBottom: 24,
              }}
            >
              Early Access Program
            </div>
            <h2 style={{ fontSize: isMobileLayout ? 28 : 38, fontWeight: 900, color: '#0f172a', marginBottom: 16 }}>
              Start with one property — not a full process change.
            </h2>
            <p style={{ fontSize: 17, color: '#64748b', lineHeight: 1.7, marginBottom: 32, maxWidth: 600, margin: '0 auto 32px' }}>
              CoreBids is in an early access program for property managers. You do not need to change your vendor relationships or commit to anything. Pilot it on a single project — use us as a competitive check, a backup option, or for work where your regular contractors are unavailable.
            </p>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: isMobileLayout ? '1fr' : 'repeat(3, 1fr)',
                gap: 16,
                marginBottom: 40,
              }}
            >
              {[
                { stat: 'Free', label: 'PM account — no setup fee' },
                { stat: '1 property', label: 'Start small, expand when ready' },
                { stat: 'No exclusivity', label: 'Keep your existing vendors' },
              ].map((s) => (
                <div key={s.stat} style={{ background: '#f8fafc', borderRadius: 12, padding: '20px 16px', border: '1px solid #e2e8f0' }}>
                  <div style={{ fontSize: 24, fontWeight: 900, color: BRAND_GREEN, marginBottom: 4 }}>{s.stat}</div>
                  <div style={{ fontSize: 13, color: '#64748b' }}>{s.label}</div>
                </div>
              ))}
            </div>
            <button
              className="cb-pm-btn-primary"
              onClick={handlePrimaryCta}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '16px 36px',
                background: BRAND_GREEN,
                color: '#fff',
                border: 'none',
                borderRadius: 12,
                fontSize: 16,
                fontWeight: 700,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
            >
              Sign Up Now
              <ArrowRight size={18} />
            </button>
          </div>
        </ContentLimiter>
      </section>
      {/* CB:END pm-channel-who-for-section */}

      {/* FAQ */}
      <section
        id="pm-faq"
        style={{ padding: isMobileLayout ? '64px 16px' : '100px 40px', background: '#f8fafc' }}
      >
        <ContentLimiter>
          <div style={{ maxWidth: 860, margin: '0 auto' }}>
            <h2
              style={{
                fontSize: isMobileLayout ? 30 : 44,
                fontWeight: 900,
                color: '#0f172a',
                textAlign: 'center',
                marginBottom: 16,
              }}
            >
              Frequently Asked Questions
            </h2>
            <p
              style={{
                fontSize: 18,
                color: '#64748b',
                textAlign: 'center',
                marginBottom: 48,
                maxWidth: 560,
                margin: '16px auto 48px',
              }}
            >
              Everything you need to know before your first project.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {faqs.map((faq, idx) => {
                const isOpen = openFaqIndex === idx;
                return (
                  <div
                    key={idx}
                    style={{
                      background: '#fff',
                      border: `2px solid ${isOpen ? BRAND_GREEN : '#e2e8f0'}`,
                      borderRadius: 16,
                      overflow: 'hidden',
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <button
                      onClick={() => setOpenFaqIndex(isOpen ? null : idx)}
                      style={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: isMobileLayout ? 20 : 24,
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        textAlign: 'left',
                      }}
                      aria-expanded={isOpen}
                    >
                      <span
                        style={{
                          fontSize: isMobileLayout ? 16 : 18,
                          fontWeight: 600,
                          color: '#0f172a',
                          flex: 1,
                          paddingRight: 16,
                        }}
                      >
                        {faq.q}
                      </span>
                      <span
                        style={{
                          fontSize: '1.5rem',
                          fontWeight: 700,
                          color: BRAND_GREEN,
                          flexShrink: 0,
                          minWidth: 24,
                          textAlign: 'center',
                        }}
                      >
                        {isOpen ? '−' : '+'}
                      </span>
                    </button>
                    {isOpen && (
                      <div
                        style={{
                          padding: `0 ${isMobileLayout ? 20 : 24}px ${isMobileLayout ? 20 : 24}px`,
                          fontSize: isMobileLayout ? 15 : 16,
                          color: '#64748b',
                          lineHeight: 1.6,
                        }}
                      >
                        {faq.a}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </ContentLimiter>
      </section>

      {/* CTA footer */}
      <section
        style={{
          padding: isMobileLayout ? '72px 16px' : '100px 40px',
          background: 'linear-gradient(135deg, #123c3b 0%, #0f5251 100%)',
          textAlign: 'center',
        }}
      >
        <ContentLimiter>
          <div style={{ maxWidth: 680, margin: '0 auto' }}>
            <h2
              style={{
                fontSize: isMobileLayout ? 30 : 44,
                fontWeight: 900,
                color: '#fff',
                marginBottom: 20,
                lineHeight: 1.2,
              }}
            >
              Ready to simplify your portfolio operations?
            </h2>
            <p
              style={{
                fontSize: isMobileLayout ? 17 : 20,
                color: 'rgba(255,255,255,0.85)',
                marginBottom: 40,
                lineHeight: 1.6,
              }}
            >
              Start with one property. There is no commitment and no process change required — see the difference before you roll it out portfolio-wide.
            </p>
            <button
              className="cb-pm-btn-primary"
              onClick={handlePrimaryCta}
              style={{
                fontSize: isMobileLayout ? 18 : 20,
                fontWeight: 700,
                padding: isMobileLayout ? '16px 22px' : '20px 52px',
                backgroundColor: '#fff',
                color: BRAND_GREEN,
                border: 'none',
                borderRadius: 12,
                cursor: 'pointer',
                boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                transition: 'all 0.3s ease',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 12,
                minHeight: 44,
              }}
            >
              Sign Up Now <ArrowRight size={22} />
            </button>
            <p style={{ marginTop: 20, fontSize: 14, color: 'rgba(255,255,255,0.55)' }}>
              Managing a larger portfolio?{' '}
              <a
                href="mailto:hello@corebids.com"
                style={{ color: 'rgba(255,255,255,0.75)', textDecoration: 'underline' }}
              >
                Reach out directly
              </a>{' '}
              and we will set you up personally.
            </p>
          </div>
        </ContentLimiter>
      </section>

      {/* Footer */}
      <footer
        style={{
          padding: '24px 40px',
          background: '#0f172a',
          textAlign: 'center',
          fontSize: 13,
          color: '#94a3b8',
        }}
      >
        <ContentLimiter>
          <div>
            &copy; {new Date().getFullYear()} CoreBids &mdash;{' '}
            <button
              onClick={() => onNavigate('terms')}
              style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', textDecoration: 'underline', fontSize: 13 }}
            >
              Terms
            </button>
            {' | '}
            <button
              onClick={() => onNavigate('privacy-policy')}
              style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', textDecoration: 'underline', fontSize: 13 }}
            >
              Privacy
            </button>
          </div>
        </ContentLimiter>
      </footer>
    </div>
  );
};

export default PropertyManagerLanding;
// CB:END pm-channel-landing
